import {
  Button,
  IconAttention,
  IconDone,
  Loader,
  ModalGeneric,
  ModalWrapper,
  styled,
} from '@payright/web-components';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../reducers';
import { useEffect, useState } from 'react';
import { AppThunk, AppThunkDispatch } from '../../../util/store';

type ModalVerifyBankStatementsProps = {
  onComplete: () => void;
  validateBsoReceived: (checkoutId: string, customerId: string) => AppThunk<Promise<boolean>>;
};

type ModalContentProps = {
  isLoading: boolean;
  isSuccessful: boolean;
  errorMessage: string | null;
};

const StyledModalBody = styled.article<{ paddingBottom?: string }>`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  width: 100%;
  max-width: 25rem;
  padding-bottom: ${(props) => props.paddingBottom};
`;

const ModalContent = ({ errorMessage, isLoading, isSuccessful }: ModalContentProps) => {
  if (isLoading) {
    return (
      <StyledModalBody>
        <Loader text="Hang tight, we're getting your bank statements" />
      </StyledModalBody>
    );
  }

  if (isSuccessful) {
    return (
      <StyledModalBody>
        <IconDone width="2rem" height="2rem" />
        <div>Got your bank statements! Taking you to the next step…</div>
      </StyledModalBody>
    );
  }

  return (
    <StyledModalBody paddingBottom="1rem">
      <div>
        <IconAttention width="3rem" height="3rem" />
      </div>
      <div>{errorMessage}</div>
    </StyledModalBody>
  );
};

const MG = styled(ModalGeneric)`
  .modal-head {
    text-align: center;
  }
`;

export const ModalVerifyBankStatements = ({
  onComplete,
  validateBsoReceived,
}: ModalVerifyBankStatementsProps) => {
  const [isPolling, setIsPolling] = useState(true);
  const [pollingError, setPollingError] = useState<string | null>(null);
  const [isSuccessful, setIsSuccessful] = useState(false);

  const dispatch = useDispatch<AppThunkDispatch>();

  const globalAuthState = useSelector((state: RootState) => state.auth);
  const globalCheckoutState = useSelector((state: RootState) => state.checkout);
  const globalCustomerState = useSelector((state: RootState) => state.customer);

  const customerId = globalAuthState.customerId || '';
  const checkoutId = globalCheckoutState.checkout?.id ?? '';
  const errorMessage = globalCustomerState.errorMessage;

  const showReloadButton = !isPolling && !isSuccessful;

  useEffect(
    () => {
      dispatch(validateBsoReceived(checkoutId, customerId)).then((result) => {
        setIsPolling(false);

        if (result) {
          setIsSuccessful(true);
          onComplete();
          return;
        }

        setPollingError(
          "Looks like we haven't received your Bank Statements, please try submitting your statements again, and make sure to click 'Finish'."
        );
      });
    },
    [] /** yes empty array, only once */
  );

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <ModalWrapper open={true} background="dark">
      <MG
        title={`${isPolling ? 'Fetching ' : ''} Your Bank Statements`}
        content={
          <ModalContent
            isLoading={isPolling}
            isSuccessful={isSuccessful}
            errorMessage={pollingError || errorMessage || null}
          />
        }
        secondaryButton={
          showReloadButton ? (
            <Button outline colour="blue" handleClick={handleReload}>
              Reload and try again
            </Button>
          ) : undefined
        }
      />
    </ModalWrapper>
  );
};
